import React from 'react';
import { useNavigate } from 'react-router-dom';
import ringArt from '../../../assets/img/portal-ring.png';
import { tintFor } from '../../../utils/tints';
import { whenLabel } from '../../../utils/surfaceFormat';
import { portalFromEvent } from '../../wall/OpenPortalButton';
import { SignalIcon } from '../../hand/icons';
import {
  bucketOf, statusPill, shotClock, ticketProgress, fundProgress, goingLabel, manageLabel, placeLine, eventPath,
} from './hostDashboardModel';

/**
 * One of YOUR EVENTS — round-3 handoff §2c. A `220px | 1fr` grid (stacked on phones): the
 * cover with its status pill, then the date, title, `CITY · VENUE`, the shot clock, two
 * progress bars (tickets black, the party fund magenta), the faces, and MANAGE → — plus, for
 * anything live, the Broadcast-as-portal ring and the share ring. Hover (pointers only, in
 * the CSS) puts the RING round it and lifts it.
 */
const money = (cents) => `$${Math.round((cents || 0) / 100).toLocaleString('en-CA')}`;

function Face({ person }) {
  return (
    <span style={{ background: tintFor(person.userId || person.displayName) }} title={person.displayName}>
      {person.avatarUrl ? <img src={person.avatarUrl} alt="" referrerPolicy="no-referrer" /> : (person.displayName || '?')[0]}
    </span>
  );
}

export default function HostEventCard({ event, now = new Date() }) {
  const navigate = useNavigate();
  const bucket = bucketOf(event.status) || 'draft';
  const [label, bg, fg] = statusPill(event.status);
  const clock = shotClock(event, now);
  const tickets = ticketProgress(event);
  const fund = fundProgress(event);
  const faces = event.faces || [];
  const live = bucket === 'live';
  const open = () => navigate(eventPath(event));
  const title = event.title || event.name;

  return (
    <article className="hd-card" data-testid="host-event-card" data-bucket={bucket}>
      <button type="button" className="hd-cover" onClick={open} aria-label={`Open ${title}`}>
        {event.posterUrl
          ? <img src={event.posterUrl} alt="" />
          : <span className="hd-cover-blank" aria-hidden>{(title || '?')[0]}</span>}
        <span className="hd-status" style={{ background: bg, color: fg }}>{label}</span>
        {event.isCoHost && <span className="hd-cohost">CO-HOST</span>}
      </button>
      <div className="hd-body">
        <div className="hd-body-top">
          <span className="hd-title-stack">
            <span className="hd-when">{event.dateTime ? whenLabel(event.dateTime) : 'NO DATE YET'}</span>
            <h3 className="hd-event-title">{title}</h3>
            {placeLine(event) && <span className="hd-place">{placeLine(event)}</span>}
          </span>
          <span className="hd-clock" data-tone={clock.tone} data-testid="shot-clock">
            <b>{clock.tag}</b>
            <span>{clock.label}</span>
          </span>
        </div>
        <div className="hd-bars">
          <span className="hd-bar">
            <span className="hd-bar-head"><span>TICKETS</span><b>{tickets.sold}<small> / {tickets.cap || '—'}</small></b></span>
            <span className="hd-track"><i style={{ width: `${tickets.pct}%` }} /></span>
          </span>
          <span className="hd-bar" data-fund="">
            <span className="hd-bar-head"><span>EVENT FUND</span><b>{money(fund.cents)}<small> ({fund.pct}%)</small></b></span>
            <span className="hd-track"><i style={{ width: `${fund.pct}%` }} /></span>
          </span>
        </div>
        <div className="hd-foot">
          <span className="hd-going" data-empty={tickets.sold ? undefined : ''}>
            {faces.length > 0 && (
              <span className="hd-faces">{faces.slice(0, 3).map((f, i) => <Face key={f.userId || i} person={f} />)}</span>
            )}
            <span>{goingLabel(tickets.sold)}</span>
          </span>
          <span className="hd-foot-actions">
            <button type="button" className="hd-manage" data-dark={bucket === 'draft' ? '' : undefined} onClick={open}>
              {manageLabel(bucket)}
            </button>
            {live && (
              <>
                <button
                  type="button"
                  className="hd-ring-btn"
                  data-dark=""
                  aria-label="Broadcast as a portal"
                  title="Broadcast as a portal"
                  onClick={() => navigate('/portals/new', { state: { fromEvent: portalFromEvent(event) } })}
                >
                  <i style={{ backgroundImage: `url(${ringArt})` }} />
                  <span><SignalIcon size={15} /></span>
                </button>
                <button
                  type="button"
                  className="hd-ring-btn"
                  aria-label="Share"
                  title="Share"
                  onClick={() => navigate(`/host/events/${event.id}/share`)}
                >
                  <i style={{ backgroundImage: `url(${ringArt})` }} />
                  <span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="square" aria-hidden="true">
                      <path d="M12 3v12M7 8l5-5 5 5M5 13v8h14v-8" />
                    </svg>
                  </span>
                </button>
              </>
            )}
          </span>
        </div>
      </div>
    </article>
  );
}
